import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ToastProvider } from '@/components/silas/SilasToast';
import App from './App';
import './index.css';
import '../static/css/light-theme.css';

const LOCAL_AUTH_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_LOCAL_AUTH_BYPASS === 'true';

// DEV-only initial load trace bootstrap markers.
// This records timestamps as early as possible in the entry module.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const w = window as Window & {
    __initialLoadTraceStartEpochMs?: number;
    __initialLoadTraceStartPerfMs?: number;
  };
  w.__initialLoadTraceStartEpochMs = w.__initialLoadTraceStartEpochMs ?? Date.now();
  w.__initialLoadTraceStartPerfMs = w.__initialLoadTraceStartPerfMs ?? performance.now();
}

function suppressReceivingEndNoise() {
  if (typeof window === 'undefined') return;

  const needle = 'Could not establish connection. Receiving end does not exist.';

  const isNoise = (value: unknown): boolean => {
    if (!value) return false;
    if (typeof value === 'string') return value.includes(needle);
    if (value instanceof Error) return value.message.includes(needle);
    return false;
  };

  window.addEventListener('unhandledrejection', (event) => {
    if (isNoise(event.reason)) {
      event.preventDefault();
    }
  });

  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    if (args.some(isNoise)) return;
    originalConsoleError(...args);
  };
}

suppressReceivingEndNoise();

function installDevFetchStormWarning() {
  if (!import.meta.env.DEV) return;
  if (typeof window === 'undefined') return;

  const w = window as Window & { __fetchStormWarningInstalled?: boolean };
  if (w.__fetchStormWarningInstalled) return;
  w.__fetchStormWarningInstalled = true;

  const WINDOW_MS = 5000;
  const THRESHOLD = 4;

  type Hit = { t: number; method: string; hint: string };
  const hitsByUrl = new Map<string, Hit[]>();
  const lastWarnAt = new Map<string, number>();

  const canonicalKey = (rawUrl: string): string => {
    try {
      const u = new URL(rawUrl, window.location.origin);
      const entries = Array.from(u.searchParams.entries());
      entries.sort(([ak, av], [bk, bv]) => ak.localeCompare(bk) || av.localeCompare(bv));

      u.search = '';
      for (const [k, v] of entries) u.searchParams.append(k, v);
      return u.toString();
    } catch {
      return rawUrl;
    }
  };

  const stackHint = (): string => {
    try {
      const stack = new Error().stack ?? '';
      const lines = stack.split('\n').map((l) => l.trim());
      const interesting = lines.find(
        (l) =>
          l &&
          !l.includes('installDevFetchStormWarning') &&
          !l.includes('window.fetch') &&
          !l.includes('/node_modules/') &&
          !l.includes('\\node_modules\\') &&
          !l.includes('client.ts') &&
          !l.includes('main.tsx'),
      );
      return interesting ?? lines.slice(0, 3).join(' | ') ?? 'unknown';
    } catch {
      return 'unknown';
    }
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method =
      (
        init?.method ||
        (input instanceof Request ? input.method : undefined) ||
        'GET'
      )?.toString() || 'GET';

    const rawUrl =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input instanceof Request
            ? input.url
            : String(input);

    const key = canonicalKey(rawUrl);
    const now = Date.now();

    const arr = hitsByUrl.get(key) ?? [];
    arr.push({ t: now, method: method.toUpperCase(), hint: stackHint() });

    // Prune old + cap size to keep memory bounded.
    const pruned = arr.filter((h) => now - h.t <= WINDOW_MS).slice(-25);
    hitsByUrl.set(key, pruned);

    if (pruned.length > THRESHOLD) {
      const last = lastWarnAt.get(key) ?? 0;
      if (now - last > WINDOW_MS) {
        lastWarnAt.set(key, now);
        const uniqueHints = Array.from(new Set(pruned.map((h) => h.hint))).slice(0, 3);
        console.warn(
          `[fetch-storm] ${pruned.length} fetches in ${WINDOW_MS}ms for ${key}\n` +
            uniqueHints.map((h) => `  - ${h}`).join('\n'),
        );
      }
    }

    return originalFetch(input as any, init);
  };
}

installDevFetchStormWarning();

// DEV-only tooling: request leaderboard (see window.__dumpRequestLeaderboard()).
// Dynamic import keeps this out of production bundles.
if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
  import('./dev/requestLeaderboard')
    .then((m) => m.installDevRequestLeaderboard())
    .catch(() => {
      /* best-effort */
    });

  // DEV-only tooling: initial load trace (see window.__dumpInitialLoadTrace()).
  import('./dev/initialLoadTrace')
    .then((m) => m.installDevInitialLoadTrace())
    .catch(() => {
      /* best-effort */
    });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000, // 30 minutes - increased to reduce BigQuery costs
      gcTime: 60 * 60 * 1000, // 60 minutes - keep in memory longer
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch if data is fresh
      refetchOnReconnect: false, // Don't refetch on network reconnect
      // Smart retry: don't retry 429 or guardrail responses to prevent retry storms
      retry: (failureCount, error) => {
        // Don't retry if we've already retried once
        if (failureCount >= 1) return false;
        // Don't retry rate limits (429), guardrails (409/422), or server overload (503)
        const status = (error as { status?: number })?.status;
        if (status === 429 || status === 409 || status === 422 || status === 503) return false;
        // Retry other errors once
        return true;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!clerkPublishableKey && !LOCAL_AUTH_BYPASS) {
  console.warn('Missing VITE_CLERK_PUBLISHABLE_KEY. Authentication will fail.');
}

const appTree = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <ToastProvider>
          <App />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  LOCAL_AUTH_BYPASS ? (
    appTree
  ) : (
    <ClerkProvider publishableKey={clerkPublishableKey} signInUrl="/login" afterSignOutUrl="/login">
      {appTree}
    </ClerkProvider>
  ),
);
